import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';
import type { GridColDef } from '@mui/x-data-grid';
import { Box, Checkbox, IconButton } from '@mui/material';
import { DeleteOutlineOutlined } from '@mui/icons-material';
import { ApiAssetBaseType, ApiAssetType, ApiOrganization, ApiOrgAssetType, ApiPaymentType } from '@agunity/api-v4';

import AddForm, { FormData } from './AddForm';
import OrgOtherHeader from '../OrgOtherHeader';
import AgTable from '../../../stories/AgTable';
import AgDropdown from '../../../stories/AgDropdown';
import { AgColor } from '../../../stories/Styles/AgColor';
import AgLoadingCard from '../../../stories/AgLoadingCard';
import AgButton from '../../../stories/Components/AgButton';
import AgNoPermission from '../../../components/AgNoPermission';
import AgTextField from '../../../stories/Components/AgTextField';

import { useModal } from '../../../redux/hooks/modal';
import { useToast } from '../../../redux/hooks/toast';
import { selectFilteredUsers } from '../../../redux/slices/global';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { ASSET_TYPES_READ_PERMISSION, ASSET_TYPES_WRITE_PERMISSION } from '../../../Constants';
import { getAssetTypesAssociatedIds, getAssetTypesList, getOrganizationItem, getOrgImage, patchOrganizationItem } from './redux';
import * as S from './styled';

const OrgPaymentTypes = () => {
  const orgId = useParams().orgId;
  const { closeModal, openModal } = useModal();
  const messages = useIntl().formatMessage;
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const users = useAppSelector(selectFilteredUsers);
  
  const [orgData, setOrgData] = useState<ApiOrganization>();
  const [assetTypes, setAssetTypes] = useState<ApiAssetType[]>([]);
  const [paymentTypes, setPaymentTypes] = useState<ApiOrgAssetType[]>([]);
  const [imageURL, setImageURL] = useState<string | undefined>('');
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(true);
  const [isSaveLoading, setIsSaveLoading] = useState(false);

  const permissions = (users?.length && users[0].permissions) || [];
  const canWrite = permissions.includes(ASSET_TYPES_WRITE_PERMISSION);
  const canRead = permissions.includes(ASSET_TYPES_READ_PERMISSION);

  const selectedAssets = useMemo(() => assetTypes.filter(({ id }) => !paymentTypes.find((pt) => pt.id === id)), [assetTypes, paymentTypes]);
  const filteredAssets = useMemo(() => selectedAssets.filter(({ baseType }) => baseType !== ApiAssetBaseType.Currency), [selectedAssets]);
  const filteredCurrencies = useMemo(() => selectedAssets.filter(({ baseType }) => baseType === ApiAssetBaseType.Currency), [selectedAssets]);

  const priceTypeOptions = useMemo(() => [
    { id: 0, name: messages({ id: 'PAYMENT_TYPE_PRICE_TYPE_0' }) },
    { id: 1, name: messages({ id: 'PAYMENT_TYPE_PRICE_TYPE_1' }) },
  ], [messages]);

  const maxDecimalPlacesOptions = useMemo(() => [
    { id: 0, name: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES_0' }) },
    { id: 1, name: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES_1' }) },
    { id: 2, name: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES_2' }) },
    { id: 3, name: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES_3' }) },
    { id: 4, name: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES_4' }) },
  ], [messages]);

  const handleGetOrg = useCallback(async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const response = await dispatch(getOrganizationItem(Number(orgId)));
      const data = response.data;
      if (data?.imageId) {
        const imageResponse = await dispatch(getOrgImage(data?.imageId!));
        const image = imageResponse?.data?.includes('data:image') 
          ? imageResponse.data 
          : 'data:image/jpeg;base64,' + imageResponse.data;
        setImageURL(image);
      }

      setOrgData(data);
      setPaymentTypes(data?.assetTypeWorkflows || []);
    } catch (error) {
      console.error('Failed to fetch organization: ', error);
    } finally {
      if (showLoader) setIsLoading(false);
    }
  }, [dispatch, orgId]);
  
  // Fetch asset types, their associated IDs, and default yield units.
  const fetchAssetTypes = useCallback(async (showLoader = true) => {
    if (!orgId) {
      console.error('Invalid Organization ID!');
      return;
    }

    if (showLoader) setIsFetching(true);

    try {
      const [{ data: assets }, { data: activeIds }] = await Promise.all([
        dispatch(getAssetTypesList()),
        dispatch(getAssetTypesAssociatedIds(Number(orgId))),
      ]);

      const filteredAssets = assets.filter(({ id }) => activeIds.includes(id!));

      setAssetTypes(filteredAssets);
    } catch (error) {
      console.error('Failed to fetch asset types:', error);
    } finally {
      if (showLoader) setIsFetching(false);
    }
  }, [dispatch, orgId]);

  const handleAddPaymentType = useCallback((data: FormData) => {
    try {
      const payload: ApiOrgAssetType = {
        assetTypeId: data?.assetType,
        currencyAssetTypeId: data?.currency,
        paymentType: data?.paymentType,
        isReceiptPassthrough: false,
        isFixedPrice: false,
        usePerUnitPrice: false,
        maxDecimalPlaces: 0,
        price: 0,
        clipFixed: null,
        clipPercent: null,
      };
      setPaymentTypes([...paymentTypes, payload]);
      closeModal();
    } catch (error) {
      console.error('Failed to add payment type:', error);
    }
  }, [paymentTypes, closeModal]);

  const handleChangePaymentType = useCallback((data: ApiOrgAssetType, field: string, value: unknown) => {
    try {
      let dataValue = value;

      if (field.includes('clip')) {
        dataValue = value ? Number(value) : null;
      }

      if (field === 'usePerUnitPrice') {
        dataValue = !!priceTypeOptions.find(({ name }) => name === value)?.id;
      }

      if (field === 'maxDecimalPlaces') {
        dataValue = maxDecimalPlacesOptions.find(({ name }) => name === value)?.id;
      }

      const newData = { ...data, [field]: dataValue };
      const results = paymentTypes.reduce((acc, pt) => {
        if (pt.assetTypeId === data.assetTypeId) return [...acc, newData];
        return [...acc, pt];
      }, [] as ApiOrgAssetType[]);

      setPaymentTypes(results);
    } catch (error) {
      console.error('Failed to update payment type:', error);
    }
  }, [maxDecimalPlacesOptions, paymentTypes, priceTypeOptions]);

  const handleDeletePaymentType = useCallback((id: number) => {
    const results = paymentTypes.filter(({ id: ptId }) => ptId !== id);
    setPaymentTypes(results);
  }, [paymentTypes]);

  const handleAddModal = useCallback(() => {
    openModal({
      title: messages({ id: 'ADD_PAYMENT_TYPE' }),
      body: <AddForm 
        assetTypes={filteredAssets} 
        currencies={filteredCurrencies}
        onCancel={() => closeModal()} 
        onSubmit={handleAddPaymentType} 
      />
    });
  }, [openModal, messages, filteredAssets, filteredCurrencies, handleAddPaymentType, closeModal]);

  const handleSave = useCallback(async () => {
    try {
      setIsSaveLoading(true);
      const payload: ApiOrganization = {
        ...orgData,
        assetTypeWorkflows: paymentTypes,
      };
      
      await dispatch(patchOrganizationItem(Number(orgId), payload));
      await handleGetOrg(false);
      await fetchAssetTypes(false);
      toast('success', messages({ id: 'PAYMENT_TYPES_SAVED_SUCCESS' }), { temporary: true });
      return paymentTypes;
    } catch (error) {
      toast('error', messages({ id: 'PAYMENT_TYPES_SAVED_ERROR' }), { temporary: true });
      console.error('Failed to save payment types:', error);
    } finally {
      setIsSaveLoading(false);
    }
  }, [dispatch, fetchAssetTypes, handleGetOrg, messages, orgData, orgId, paymentTypes, toast]);

  const columns = useMemo(
    () => [
      {
        field: 'id',
        headerName: messages({ id: 'PAYMENT_TYPE_ID' }),
        valueGetter: ({ row }) => row.assetTypeId,
      },
      {
        field: 'name',
        headerName: messages({ id: 'PAYMENT_TYPE_NAME' }),
        minWidth: 264,
        valueGetter: ({ row }) => assetTypes.find(({ id }) => id === row.assetTypeId)?.name,
      },
      {
        field: 'currencyAssetTypeId',
        headerName: messages({ id: 'PAYMENT_TYPE_CURRENCY' }),
        minWidth: 150,
        valueGetter: ({ value }) => assetTypes.find(({ id }) => id === value)?.name,
      },
      {
        field: 'paymentType',
        headerName: messages({ id: 'PAYMENT_TYPE' }),
        minWidth: 150,
        valueGetter: ({ value }) => messages({ id: `PAYMENT_TYPE_${value}` }),
      },
      {
        field: 'isFixedPrice',
        headerName: messages({ id: 'PAYMENT_TYPE_FIXED_PAYMENT' }),
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const isDisabled = row.paymentType === ApiPaymentType.ReceiptProduce || row.paymentType === ApiPaymentType.NoPayment;
          return isDisabled ? null : (
            <Checkbox 
              color='primary'
              checked={row.isFixedPrice} 
              onChange={(_, isChecked) => (canWrite && !row.copiedFrom) && handleChangePaymentType(row, 'isFixedPrice', isChecked)} 
            />
          );
        }
      },
      {
        field: 'isReceiptPassthrough',
        headerName: messages({ id: 'PAYMENT_TYPE_RECEIPT_PASSTHROUGH' }),
        minWidth: 120,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const isDisabled = row.paymentType !== ApiPaymentType.ReceiptProduce;
          return isDisabled ? null : (
            <Checkbox 
              color='primary'
              checked={row.isReceiptPassthrough} 
              onChange={(_, isChecked) => (canWrite && !row.copiedFrom)  && handleChangePaymentType(row, 'isReceiptPassthrough', isChecked)} 
            />
          );
        }
      },
      {
        field: 'usePerUnitPrice',
        headerName: messages({ id: 'PAYMENT_TYPE_PRICE_TYPE' }),
        minWidth: 200,
        flex: 1,
        renderCell: ({ row }) => {
          const isDisabled = row.paymentType === ApiPaymentType.NoPayment;
          return isDisabled ? null : (
            <AgDropdown 
              disabled={!canWrite || !!row.copiedFrom}
              options={priceTypeOptions}
              value={priceTypeOptions.find(({ id }) => Boolean(id) === row.usePerUnitPrice)?.name}
              onChange={(value: string) => handleChangePaymentType(row, 'usePerUnitPrice', value)}
              sx={{ m: 0 }}
            />
          );
        }
      },
      {
        field: 'maxDecimalPlaces',
        headerName: messages({ id: 'PAYMENT_TYPE_DECIMAL_PLACES' }),
        minWidth: 200,
        flex: 1,
        renderCell: ({ row }) => (
          <AgDropdown 
            disabled={!canWrite || !!row.copiedFrom}
            options={maxDecimalPlacesOptions}
            value={maxDecimalPlacesOptions.find(({ id }) => id === row.maxDecimalPlaces)?.name}
            onChange={(value: string) => handleChangePaymentType(row, 'maxDecimalPlaces', value)}
            sx={{ m: 0 }}
          />
        )
      },
      {
        field: 'clipFixed',
        headerName: messages({ id: 'PAYMENT_TYPE_CLIP_FIXED' }),
        minWidth: 100,
        flex: 1,
        renderCell: ({ row }) => {
          const isDisabled = row.paymentType !== ApiPaymentType.ReceiptProduce;
          return isDisabled ? null : (
            <AgTextField 
              type='number'
              placeholder=''
              disabled={!canWrite || !!row.copiedFrom}
              value={row.clipFixed ?? ''}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const value = event.target.value.replace(/[^0-9.]/g, '');
                const isValid = /^\d+(\.\d{1,2})?$/.test(value);
                if (!value || isValid) handleChangePaymentType(row, 'clipFixed', value);
              }}
            />
          );
        }
      },
      {
        field: 'clipPercent',
        headerName: messages({ id: 'PAYMENT_TYPE_CLIP_PERCENTAGE' }),
        minWidth: 100,
        flex: 1,
        renderCell: ({ row }) => {
          const isDisabled = row.paymentType !== ApiPaymentType.ReceiptProduce;
          return isDisabled ? null : (
          <AgTextField 
            type='number'
            placeholder=''
            unitRight='%'
            disabled={!canWrite || !!row.copiedFrom}
            value={row.clipPercent ?? ''}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              const value = event.target.value.replace(/[^0-9.]/g, '');
              const isValid = /^(100(\.0{1,2})?|[1-9]?\d(\.\d{1,2})?|0(\.\d{1,2})?)$/.test(value);
              if (!value || isValid) handleChangePaymentType(row, 'clipPercent', value);
            }}
          />
          );
        }
      },
      {
        field: 'actions',
        headerName: messages({ id: 'ACTIONS' }),
        minWidth: 80,
        sortable: false,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => (
          <IconButton disabled={!canWrite} onClick={() => handleDeletePaymentType(row?.id)}>
            <DeleteOutlineOutlined fontSize='small' color='error' />
          </IconButton>
        )
      }
    ] as GridColDef[],
    [assetTypes, canWrite, maxDecimalPlacesOptions, handleChangePaymentType, handleDeletePaymentType, messages, priceTypeOptions]
  );

  useEffect(() => {
    const debounce = setTimeout(() => {
      handleGetOrg();
      fetchAssetTypes();
    });

    return () => clearTimeout(debounce);
  }, [handleGetOrg, fetchAssetTypes]);

  if (isLoading) return <AgLoadingCard text={messages({ id: 'LOADING' })} />;

  if (!canWrite && !canRead) return <AgNoPermission />;

  return (
    <S.Main>
      <OrgOtherHeader 
        orgId={orgId!} 
        orgData={orgData!} 
        imageURL={imageURL!}
        label={messages({ id: 'PAYMENT_TYPE' })}
        description={messages({ id: 'PAYMENT_TYPE_DESCRIPTION' })}
      />

    <Box sx={{ 
      mt: 3, 
      '.MuiDataGrid-main': { 
        height: 'calc(100vh - 365px) !important' 
      },
      '.MuiInputBase-input, input': { 
        fontSize: '13px !important',
        color: AgColor.grey7
      }
    }}>
      <S.Container>
        <AgTable
          showToolbar
          type='generic'
          disabledCheckbox
          columns={columns}
          initialRows={paymentTypes}
          tableLoading={isLoading || isFetching || isSaveLoading}
          handleAddNewButton={handleAddModal}
          addNewButtonDisabled={!canWrite || !filteredAssets.length || isSaveLoading}
          setSort={() => {}}
          allowToSelectBoolean
          showTableMenu={false}
          noRowsOverlayContent={messages({ id: 'GENERIC_NO_TABLE_ROWS' })}
          disableRowSelectionOnClick
          disableRowSelectionOnCheck
          getRowId={(row) => row.assetTypeId}
          customElement={(
            <AgButton
              label={messages({ id: 'SAVE' })}
              width={'105px'}
              style={{ padding: '4px', fontSize: '10px' }}
              onClick={handleSave}
              loading={isSaveLoading}
              disabled={!canWrite || isSaveLoading}
            />
          )}
        />
      </S.Container>
    </Box>
    </S.Main>
  );
};

export default OrgPaymentTypes;